import React, { useState, useMemo, useCallback } from "react";
import { Checkbox } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import PropTypes from "prop-types";

import { getMassDisabled, getVisibleEntities } from "../../../../utils/formViews";
import { getCriticalRolesSearchHelp, getSystemsSearchHelp } from "../../../../utils/searchHelps";

import { createRowCellContentInner } from "../../../common/table";
// import useLocalTable, { ComplexFilter, Filter } from "../../../common/hooks/useLocalTable";
import { TrashIcon } from "../../../common/icons";
import { SearchHelpInput } from "../../../common/form-controls";
import SearchHelpDialog from "../../../common/search-helps";
import { IconButton } from "../../../common/buttons";
import { EntityFormViewTable } from "../../../common/entity-form";
import useCriticalRolesTable from "./useCriticalRolesTable";

const getErrorFromValidationErrors = (row, columnId) => {
    return row && row[`error_${columnId}`];
};

// const getFilters = (searchString) => {
//     const filters = [
//         new ComplexFilter(
//             [
//                 new Filter("system", Filter.CO, searchString),
//                 new Filter("criticalRole", Filter.CO, searchString),
//                 new Filter("description", Filter.CO, searchString),
//             ],
//             ComplexFilter.OR
//         ),
//     ];

//     return filters;
// };

const getCellValue = (row, column) => {
    switch (column.id) {
        default:
            return row[column.id];
    }
};

const CriticalRolesView = ({
    criticalRoles,
    addCriticalRole,
    deleteCriticalRole,
    deleteCriticalRoleMass,
    editCriticalRole,
    disabled,
    errorIndex,
    setErrorScroll,
}) => {
    const intl = useIntl();

    // const [searchString, setSearchString] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);
    const [rowAction, setRowAction] = useState(null);
    const visibleRole = getVisibleEntities(criticalRoles);
    const rolesTable = useCriticalRolesTable(visibleRole);

    const columnTexts = useMemo(
        () => ({
            system: intl.formatMessage({ id: "matrix-headers-form-content.subtable-column.criticalRoles.system" }),
            criticalRole: intl.formatMessage({ id: "matrix-headers-form-content.subtable-column.criticalRoles.criticalRole" }),
            description: intl.formatMessage({ id: "matrix-headers-form-content.subtable-column.criticalRoles.description" }),
            active: intl.formatMessage({ id: "matrix-headers-form-content.subtable-column.active" }),
        }),
        [intl]
    );

    const setSearchString = useCallback(
        (value) => {
            rolesTable.setSearchString(value);
        },
        [rolesTable]
    );

    const setFilterData = useCallback(
        (value) => {
            rolesTable.setFilterData(value);
        }, [rolesTable]
    )

    const toolbarActions = useMemo(() => {
        const massDisabled = getMassDisabled(disabled, selectedIds);

        return [
            {
                id: "mass-delete",
                title: intl.formatMessage({ id: "common.btn-delete" }),
                callback: () => {
                    deleteCriticalRoleMass(selectedIds);
                    setSelectedIds([]);
                },
                position: "end",
                variant: "contained",
                disabled: massDisabled,
            },
            {
                id: "search",
                type: "search",
                position: "end",
                value: rolesTable?.searchString,
                callback: setSearchString,
            },
            {
                id: "add",
                title: <FormattedMessage id="criticalRole-view.btn-create-criticalRole" />,
                callback: () => {
                    addCriticalRole();
                    setSearchString("");
                    setFilterData([])
                },
                position: "begin",
                disabled,
            },
        ];
    }, [addCriticalRole, rolesTable, disabled, selectedIds, deleteCriticalRoleMass, intl, setSearchString, setFilterData]);

    const createRowCellContent = useCallback(
        (row, column, selected, onSelect) => {
            const error = getErrorFromValidationErrors(row, column.id);

            switch (column.type) {
                case "search-help": {
                    return (
                        <SearchHelpInput
                            disabled={disabled}
                            error={error ? row[column.id] === error.value && error["type"] === "validation" : false}
                            errorMessage={error && error.message}
                            warning={error ? row[column.id] === error.value && error["type"] === "integration_warning" : false}
                            warningMessage={error && error.message}
                            showFormHelpers={false}
                            showInnerHelpers
                            value={row[column.id]}
                            fullWidth
                            onOpenSearchHelp={() => {
                                setRowAction({
                                    type: "search-help",
                                    row: row,
                                    field: column.id,
                                });
                            }}
                            onChange={(e) => {
                                const value = e.target.value;
                                editCriticalRole(row, column.id, value);
                            }}
                        />
                    );
                }

                case "delete":
                    return (
                        <IconButton
                            disabled={disabled}
                            onClick={() => deleteCriticalRole(row.id)}
                            sx={{
                                border: "1px solid #B7B7B7",
                                borderRadius: "3px",
                                width: "34px",
                                height: "34px",
                            }}
                        >
                            <TrashIcon color="#B7B7B7" width="34px" height="34px" />
                        </IconButton>
                    );

                case "boolean":
                    return (
                        <Checkbox
                            disabled={disabled}
                            checked={row[column.id]}
                            sx={{
                                "&.Mui-checked": {
                                    color: "gray",
                                },
                            }}
                            onClick={() => {
                                editCriticalRole(row, column.id, !row[column.id]);
                            }}
                        />
                    );

                default:
                    return createRowCellContentInner(row, column, selected, onSelect, getCellValue);
            }
        },
        [editCriticalRole, deleteCriticalRole, disabled]
    );

    const columns = useMemo(
        () => [
            {
                id: "select",
                type: "select",
            },
            {
                id: "system",
                title: columnTexts.system,
                type: "search-help",
                filterable: true,
            },
            {
                id: "roleName",
                title: columnTexts.criticalRole,
                type: "search-help",
                filterable: true,
            },
            {
                id: "description",
                title: columnTexts.description,
                type: "text",
                width: "40%",
            },
            {
                id: "active",
                title: columnTexts.active,
                type: "boolean",
            },
            {
                id: "delete",
                type: "delete",
            },
        ],
        [columnTexts]
    );

    const getSearchHelpData = useCallback(
        (params, signal) => {
            switch (rowAction?.field) {
                case "system":
                    return getSystemsSearchHelp(params, signal);

                case "roleName":
                    return getCriticalRolesSearchHelp(params, signal, rowAction?.row?.system);
            }
        },
        [rowAction]
    );

    const searchHelpColumns = {
        system: [
            { id: "key", title: columnTexts.system },
            { id: "text", title: columnTexts.description },
        ],
        roleName: [
            { id: "key", title: columnTexts.criticalRole },
            { id: "text", title: columnTexts.description },
        ],
    };

    // const {
    //     rows, onFilter
    // } = useLocalTable(criticalRoles, false);

    const handleSelectRow = useCallback((row, value) => {
        if (value) {
            setSelectedIds((selectedIds) => [...selectedIds, row.id]);
            return;
        }

        setSelectedIds((selectedIds) => selectedIds.filter((id) => id !== row.id));
    }, []);

    const handleSelectAll = useCallback(
        (value) => {
            if (!value) {
                setSelectedIds([]);
                return;
            }

            setSelectedIds(rolesTable.rows.map((role) => role.id));
        },
        [rolesTable]
    );

    return (
        <>
            <EntityFormViewTable
                rows={rolesTable.rows}
                columns={columns}
                toolbarActions={toolbarActions}
                selectedIds={selectedIds}
                onSelectRow={handleSelectRow}
                onSelectAll={handleSelectAll}
                createRowCellContent={createRowCellContent}
                errorIndex={errorIndex}
                setErrorScroll={setErrorScroll}
                filterData={rolesTable.filterData}
                setFilterData={rolesTable.setFilterData}
                getFilters={rolesTable.getDialogFilters}
                setSearchString={setSearchString}
                getDialogFilters={rolesTable.getDialogFilters}
                searchString={rolesTable.searchString}
                getCellValue={getCellValue}
            />

            {rowAction?.type === "search-help" && (
                <SearchHelpDialog
                    defaultSearchString={rowAction.row[rowAction.field] || ""}
                    open={rowAction && rowAction.type === "search-help"}
                    readRows={getSearchHelpData}
                    columns={searchHelpColumns[rowAction.field]}
                    selectOneRow={true}
                    onSubmit={(selectedRow) => {
                        editCriticalRole(rowAction.row, rowAction.field, selectedRow.key);
                        setRowAction(null);
                    }}
                    onCancel={() => {
                        setRowAction(null);
                    }}
                />
            )}
        </>
    );
};

CriticalRolesView.propTypes = {
    criticalRoles: PropTypes.arrayOf(PropTypes.object),
    addCriticalRole: PropTypes.func,
    deleteCriticalRole: PropTypes.func,
    deleteCriticalRoleMass: PropTypes.func,
    editCriticalRole: PropTypes.func,
    disabled: PropTypes.bool,
    validationErrors: PropTypes.object,
    errorIndex: PropTypes.number,
    setErrorScroll: PropTypes.func,
};

export default CriticalRolesView;
